import { useEffect, useState } from 'react';
import { TypingDots } from '../ui';
import { Button } from '../ui/Button';

// --- Shadow Mode Theatre (right side) ---

interface ChatBubbleProps {
  role: 'client' | 'ai' | 'operator';
  text: string;
  visible: boolean;
  delay: number;
}

function ChatBubble({ role, text, visible, delay }: ChatBubbleProps): JSX.Element {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!visible) { setShow(false); return; }
    const timer = setTimeout(() => setShow(true), delay);
    return () => clearTimeout(timer);
  }, [visible, delay]);

  const roleStyles: Record<typeof role, { bubble: string; label: string; labelText: string }> = {
    client: {
      bubble: 'bg-white border border-border-subtle',
      label: 'bg-surface-subtle text-text-secondary',
      labelText: 'Клиент',
    },
    ai: {
      bubble: 'bg-accent-brand-light border border-accent-brand-border',
      label: 'bg-accent-ai-light text-accent-ai',
      labelText: 'Shadow Draft',
    },
    operator: {
      bubble: 'bg-white border border-accent-success/30',
      label: 'bg-accent-success-light text-accent-success',
      labelText: 'Оператор',
    },
  };

  const style = roleStyles[role];

  return (
    <div
      className={`transition-all duration-500 ease-out ${
        show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
      }`}
    >
      <span className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded-md mb-1 ${style.label}`}>
        {style.labelText}
      </span>
      <div className={`rounded-card px-3 py-2.5 text-sm leading-relaxed ${style.bubble}`}>
        {text}
      </div>
    </div>
  );
}

function MatchIndicator({ visible }: { visible: boolean }): JSX.Element {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!visible) { setShow(false); return; }
    const timer = setTimeout(() => setShow(true), 400);
    return () => clearTimeout(timer);
  }, [visible]);

  return (
    <div
      className={`flex items-center gap-2 transition-all duration-500 ease-out ${
        show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
      }`}
    >
      <div className="h-px flex-1 bg-accent-success/30" />
      <div className="flex items-center gap-1.5 bg-accent-success-light border border-accent-success/30 rounded-full px-3 py-1">
        <svg className="w-3.5 h-3.5 text-accent-success" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
        </svg>
        <span className="text-xs font-semibold text-accent-success">Близко к стилю</span>
        <span className="text-[10px] text-accent-success/70">84%</span>
      </div>
      <div className="h-px flex-1 bg-accent-success/30" />
    </div>
  );
}

/** Задержка перед показом индикатора — сами точки берём из ui/TypingDots */
function DelayedTypingDots({ visible }: { visible: boolean }): JSX.Element {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!visible) { setShow(false); return; }
    const timer = setTimeout(() => setShow(true), 200);
    return () => clearTimeout(timer);
  }, [visible]);

  if (!show) return <div className="h-6" />;

  return (
    <div className="flex items-center gap-1 px-3 py-2">
      <span className="inline-block text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-accent-ai-light text-accent-ai mb-1">
        Anima думает
      </span>
      <TypingDots />
    </div>
  );
}

function ShadowTheatre(): JSX.Element {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const steps = [
      { delay: 800, next: 1 },
      { delay: 2200, next: 2 },
      { delay: 3800, next: 3 },
      { delay: 5800, next: 4 },
      { delay: 7400, next: 5 },
      { delay: 11000, next: 0 },
    ];

    const current = steps[step];
    if (!current) return;

    const timer = setTimeout(() => setStep(current.next), current.delay);
    return () => clearTimeout(timer);
  }, [step]);

  return (
    <div className="relative">
      <div className="absolute -inset-4 bg-gradient-to-br from-accent-brand-light/60 via-transparent to-accent-ai-light/40 rounded-[28px] blur-2xl" />

      <div className="relative bg-white/80 backdrop-blur-sm border border-border-subtle rounded-[20px] shadow-medium overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border-subtle bg-surface-base/60">
          <div className="w-2 h-2 rounded-full bg-accent-brand animate-pulse" />
          <span className="text-xs font-heading font-semibold text-text-primary">Shadow Mode</span>
          <span className="text-[10px] text-text-muted ml-auto">Демонстрация</span>
        </div>

        <div className="p-4 space-y-3 min-h-[260px]">
          <ChatBubble
            role="client"
            text="Здравствуйте, сколько стоит подключение?"
            visible={step >= 1}
            delay={0}
          />

          {step === 2 && <DelayedTypingDots visible={true} />}

          <ChatBubble
            role="ai"
            text="Добрый день! Стоимость зависит от тарифа: от 2 990 ₽/мес. Включена 14-дневная бесплатная версия Growth-плана. Подробнее на странице тарифов."
            visible={step >= 3}
            delay={0}
          />

          <ChatBubble
            role="operator"
            text="Привет! Тарифы от 2 990 ₽/мес, первые 14 дней бесплатно на Growth. Могу помочь с выбором?"
            visible={step >= 4}
            delay={0}
          />

          <MatchIndicator visible={step >= 5} />
        </div>
      </div>
    </div>
  );
}

function LandingNav(): JSX.Element {
  const [mobileOpen, setMobileOpen] = useState(false);

  const navLinks = [
    { href: '#features', label: 'Возможности' },
    { href: '#pricing', label: 'Тарифы' },
    { href: '#demo', label: 'Демо' },
  ];

  return (
    <nav className="relative flex items-center justify-between w-full">
      <a href="/" className="flex items-center gap-2 group">
        <div className="w-8 h-8 rounded-comp bg-accent-brand flex items-center justify-center transition-transform duration-200 group-hover:scale-105">
          <span className="text-sm font-bold text-white font-heading">A</span>
        </div>
        <span className="text-lg font-heading font-bold text-text-primary">Anima</span>
      </a>

      <div className="hidden md:flex items-center gap-6">
        {navLinks.map((link) => (
          <a key={link.href} href={link.href} className="text-sm text-text-secondary hover:text-text-primary transition-colors">
            {link.label}
          </a>
        ))}
        <a
          href="/login"
          className="text-sm font-medium text-accent-brand hover:opacity-80 transition-colors"
        >
          Войти
        </a>
      </div>

      <button
        className="md:hidden p-2 text-text-secondary hover:text-text-primary transition-colors"
        aria-label={mobileOpen ? 'Закрыть меню' : 'Открыть меню'}
        onClick={() => setMobileOpen(!mobileOpen)}
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
          {mobileOpen ? (
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
          )}
        </svg>
      </button>

      {mobileOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 md:hidden bg-surface-elevated border border-border-subtle rounded-card shadow-medium py-2 z-50">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="block px-5 py-2.5 text-sm text-text-secondary hover:text-text-primary hover:bg-surface-subtle transition-colors"
              onClick={() => setMobileOpen(false)}
            >
              {link.label}
            </a>
          ))}
          <div className="border-t border-border-subtle my-1" />
          <a
            href="/login"
            className="block px-5 py-2.5 text-sm font-medium text-accent-brand hover:opacity-80 transition-colors"
            onClick={() => setMobileOpen(false)}
          >
            Войти
          </a>
        </div>
      )}
    </nav>
  );
}

export function HeroSection(): JSX.Element {
  return (
    <section className="relative min-h-screen flex flex-col overflow-hidden bg-surface-base">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_rgba(79,70,229,0.04)_0%,_transparent_50%),radial-gradient(ellipse_at_bottom_right,_rgba(245,158,11,0.03)_0%,_transparent_50%)]" />

      <div className="relative z-10 w-full max-w-6xl mx-auto px-5 sm:px-8">
        <div className="pt-5 pb-4">
          <LandingNav />
        </div>

        <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center py-12 lg:py-20">
          <div className="max-w-lg">
            <div className="inline-flex items-center gap-1.5 bg-accent-brand-light border border-accent-brand-border rounded-full px-3 py-1 mb-6">
              <div className="w-1.5 h-1.5 bg-accent-brand rounded-full" />
              <span className="text-xs font-medium text-accent-brand">Shadow Mode — AI под контролем</span>
            </div>

            <h1 className="font-heading text-4xl sm:text-5xl lg:text-[3.25rem] font-extrabold text-text-primary leading-[1.1] tracking-tight">
              AI, который отвечает{' '}
              <span className="text-accent-brand">как ваша команда</span>
            </h1>

            <p className="mt-5 text-base sm:text-lg text-text-secondary leading-relaxed max-w-md">
              Подключите email, Telegram и&nbsp;виджет. AI&nbsp;учится на&nbsp;ответах операторов
              и&nbsp;постепенно берёт рутину на&nbsp;себя.{' '}
              <span className="text-text-primary font-medium">14&nbsp;дней бесплатно.</span>
            </p>

            <div className="flex flex-wrap items-center gap-3 mt-8">
              <Button variant="primary" size="xl" onClick={() => { window.location.href = '/demo'; }}>
                Попробовать бесплатно
              </Button>
              <a
                href="#demo"
                className="inline-flex items-center gap-1.5 px-5 py-3.5 text-sm font-medium text-text-secondary hover:text-text-primary transition-colors rounded-card hover:bg-surface-subtle"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.91 11.672a.375.375 0 010 .656l-5.603 3.113a.375.375 0 01-.557-.328V8.887c0-.286.307-.466.557-.327l5.603 3.112z" />
                </svg>
                Посмотреть демо
              </a>
            </div>

            <p className="mt-6 text-xs text-text-muted">
              Email · Telegram · Виджет — без привязки карты
            </p>
          </div>

          <div className="lg:pl-4">
            <ShadowTheatre />
          </div>
        </div>
      </div>
    </section>
  );
}
